/**
 * @module lib/rpdm/capabilities
 * @summary Additive-endpoint capability detection (Rev.1a · FC-2).
 * @description Explicit capability detection for the two REEDOS-approved
 * additive READ-ONLY endpoints and the embedded `reachable_states` field on
 * the WI GET response. No silent fallback: production and Preview builds
 * fail-closed with a `capability_missing` refusal when a capability is not
 * available. Only a compile-time-guarded dev/test path may substitute
 * legacy client-side joins.
 *
 * ## Purpose
 * - Ensure one deterministic runtime behaviour per environment (§ 4.4).
 * - Give the UI a single seam to detect the two additive endpoints so a
 *   staged backend rollout produces a `FailClosedPlaceholder` on the
 *   client rather than a silent fallback.
 * - Record every miss via a structured telemetry entry so a REEDOS audit
 *   can trace missing backend surfaces without needing server logs.
 *
 * ## Inputs / Outputs
 * - `detect(name)` → Promise<'available' | 'unavailable' | 'unknown'>.
 *   Cached per session; first call issues a lightweight probe (Phase 2+).
 *   In Phase 1a the module is IMPORT-PURE and DOES NOT issue any probe.
 * - `assertAvailable(name)` → Promise<void>. Throws a normalised
 *   `capability_missing` refusal when detection is `unavailable` /
 *   `unknown` AND `ALLOW_LEGACY_JOIN_FALLBACK` is `false`.
 * - `ALLOW_LEGACY_JOIN_FALLBACK` (`boolean`) — compile-time constant.
 *   `false` in production and Preview. `true` only when both
 *   `NODE_ENV=development` AND `REACT_APP_RPDM_W2UI_LEGACY_FALLBACK=true`.
 * - `_setDetectionOverride(name, status)` — test-only cache seed.
 * - `_reset()` — test-only helper.
 *
 * ## Invariants (Phase 1a · § 11.0-G, § 4.4)
 * - Side-effect free at import time. NO probe is issued, NO cache written,
 *   NO telemetry emitted merely by importing this module.
 * - `detect` is called at most once per capability per session; the
 *   probe implementation is a Phase 2+ concern. In Phase 1a it returns
 *   `'unknown'` from the cache seed.
 * - `assertAvailable` NEVER falls back silently to a legacy join in
 *   production or Preview. It throws a `capability_missing` refusal.
 * - Telemetry emission uses `console.info` with a namespaced tag; a
 *   backend audit sink can be wired in Phase 2+ without changing this
 *   module's public surface.
 *
 * ## Future consuming phase
 * - Phase 2: `hierarchy.js` `readEngagementHierarchy` calls
 *   `assertAvailable('hierarchy')` before its GET.
 * - Phase 3: `lifecycle.js` `readWorkItemWithLifecycle` checks
 *   `reachable_states_embed`.
 * - Phase 5: `effortAnalytics.js` `readDailyTimeseries` checks
 *   `effort_daily_timeseries`.
 * - § 4.6 regression suite covers production posture + dev fallback +
 *   fail-closed rendering + telemetry emission.
 */

/**
 * @typedef {'hierarchy' | 'effort_daily_timeseries' | 'reachable_states_embed'} CapabilityName
 * @typedef {'available' | 'unavailable' | 'unknown'} CapabilityStatus
 */

/**
 * Compile-time constant. Enables the documented dev/test legacy-join
 * fallback ONLY when both `NODE_ENV=development` and
 * `REACT_APP_RPDM_W2UI_LEGACY_FALLBACK=true` are set at build time.
 * CRA embeds `process.env.NODE_ENV` and any `REACT_APP_*` env var via
 * DefinePlugin, so this constant is evaluated at build time and cannot
 * be flipped at runtime.
 * @type {boolean}
 */
export const ALLOW_LEGACY_JOIN_FALLBACK = (function computeFallbackFlag() {
    try {
        const nodeEnv = (typeof process !== "undefined" && process.env && process.env.NODE_ENV) || "";
        const optIn = (typeof process !== "undefined" && process.env && process.env.REACT_APP_RPDM_W2UI_LEGACY_FALLBACK) || "";
        return nodeEnv === "development" && optIn === "true";
    } catch (_e) {
        return false;
    }
})();

/**
 * Session-scoped detection cache. Never seeded at module import.
 * @type {Map<CapabilityName, CapabilityStatus>}
 */
const detectionCache = new Map();

/**
 * Rollout ledger — capabilities whose additive backend endpoint has
 * shipped and is generally available in every reachable environment
 * (Preview, Staging, Production). Each entry here is a REEDOS-approved
 * declaration: adding a capability to this set means "the endpoint is
 * live; skip the probe and treat it as available".
 *
 * NB the ledger does NOT bypass server refusal. A `feature_disabled_404`
 * or `permission_denied_403` still surfaces via the response path and
 * the caller renders the appropriate placeholder. The ledger only
 * short-circuits the pre-flight capability check.
 *
 * @type {ReadonlySet<CapabilityName>}
 */
const GA_CAPABILITIES = new Set([
    // Phase 2 · shipped 2026-07-31 (commit 83be84d6). Additive read-only
    // endpoint `GET /api/projects/:engagementId/hierarchy`.
    "hierarchy",
]);

/**
 * Emit a namespaced telemetry entry. In Phase 1a this is `console.info`;
 * a backend audit sink can be wired in later phases without changing
 * consumer call sites.
 * @param {CapabilityName} capability
 * @param {CapabilityStatus} status
 */
function emitTelemetry(capability, status) {
    if (typeof console === "undefined") return;
    const payload = {
        ts: new Date().toISOString(),
        capability,
        status,
        environment: (typeof process !== "undefined" && process.env && process.env.NODE_ENV) || "unknown",
    };
    console.info("w2ui.capability_missing." + capability, payload);
}

/**
 * Best-effort detection of an additive endpoint's availability.
 *
 * PHASE 1a IMPORT-PURE CONTRACT: this function DOES NOT issue any network
 * probe in Phase 1a. It reads the session cache. First-call probe logic
 * (a lightweight HEAD or cached GET) lands in Phase 2 alongside the first
 * real surface.
 *
 * @param {CapabilityName} name
 * @returns {Promise<CapabilityStatus>}
 * @invariant Never throws. Returns `'unknown'` when no cache entry exists.
 * @invariant Side-effect free except for a single cache write on first
 *            resolution (Phase 2+).
 */
export async function detect(name) {
    const cached = detectionCache.get(name);
    if (cached) return cached;
    // Phase 2+: capabilities in the GA ledger are treated as available
    // without a probe. Every other capability remains 'unknown' until
    // its own rollout batch adds it.
    if (GA_CAPABILITIES.has(name)) {
        detectionCache.set(name, "available");
        return "available";
    }
    return "unknown";
}

/**
 * Assert a capability is available. Throws a normalised
 * `capability_missing` refusal when it is not.
 *
 * @param {CapabilityName} name
 * @returns {Promise<void>}
 * @throws {{ status: 424, code: 'capability_missing', message: string, retriable: false, meta: { capability: CapabilityName } }}
 *   when detection is not `'available'` AND `ALLOW_LEGACY_JOIN_FALLBACK`
 *   is `false` (i.e. always in production / Preview).
 * @invariant NEVER silently substitutes a legacy client-side join.
 * @invariant When it throws, telemetry is emitted first so the miss is
 *            auditable.
 */
export async function assertAvailable(name) {
    const status = await detect(name);
    if (status === "available") return;
    emitTelemetry(name, status);
    if (ALLOW_LEGACY_JOIN_FALLBACK) {
        // Dev/test only. Wrappers may attempt their documented legacy
        // path AFTER the surface has surfaced the "Legacy compatibility
        // mode" banner. Phase 1a wrappers never take this branch because
        // no surface consumes them yet.
        return;
    }
    throw {
        status: 424,
        code: "capability_missing",
        message: name + " not available on this environment",
        retriable: false,
        meta: { capability: name },
    };
}

/**
 * Test-only: seed the detection cache to simulate a specific environment.
 * @param {CapabilityName} name
 * @param {CapabilityStatus} status
 */
export function _setDetectionOverride(name, status) {
    detectionCache.set(name, status);
}

/**
 * Test-only: wipe the detection cache.
 */
export function _reset() {
    detectionCache.clear();
}
